import { useState, useCallback, useRef, useEffect } from "react";
import { useEagleEyeNetworkStore } from "state/zustand-stores/eagle-eye-network-store";
import { useSearchEenVideoMutation } from "graphql/mutations/search-een-video.generated";
import { useGetEenCamerasMutation } from "graphql/mutations/get-een-cameras.generated";
import { useGetEenCameraByIdMutation } from "graphql/mutations/get-een-camera-by-id.generated";
import { useListEenMediaMutation } from "graphql/mutations/list-een-media.generated";
import { auth } from "config/firebase/firebase";

export interface Camera {
  id: string;
  name: string;
  status?: string;
}

export interface EenFullFrameImageData {
  type: "een.fullFrameImageUrl.v1";
  creatorId: string;
  timestamp: string;
  httpsUrl: string;
}

export type EenEventDataItem = EenFullFrameImageData | { type: string; [key: string]: unknown };

export interface EenSearchVideoResult {
  id: string;
  startTimestamp: string;
  endTimestamp: string;
  span: boolean;
  accountId: string;
  actorId: string;
  actorAccountId: string;
  actorType: string;
  actorName: string;
  creatorId: string;
  type: string;
  matchObjectIds: string[];
  dataSchemas: string[];
  data: EenEventDataItem[];
}

export interface EenSearchVideoResponse {
  results: EenSearchVideoResult[];
}

export type MediaStreamType = "preview" | "main";
export type MediaType = "video" | "image";

export interface ListMediaParams {
  deviceId: string;
  type: MediaStreamType;
  mediaType: MediaType;
  startTimestampGte: string;
  endTimestampLte?: string;
  coalesce?: boolean;
  pageSize?: number;
  pageToken?: string;
  mostRecentOnly?: boolean;
}

export interface CameraSearchParams {
  /** Full-text search across name, ID, tags, and location metadata */
  q?: string;
  /** Case-insensitive substring match on camera name */
  name__contains?: string;
  /** Filter by connection status values (e.g. "online", "deviceOffline") */
  status__in?: string[];
  /** Filter by location IDs */
  locationId__in?: string[];
  /** Return cameras that have at least one of these tags */
  tags__any?: string[];
  /** Results per page (1–500, default 100) */
  pageSize?: number;
}

interface UseEagleEyeApiOptions {
  onUnauthorized?: () => void;
}

/**
 * Returns a Firebase ID token for the currently signed-in user.
 * Throws if no user is signed in.
 */
async function getFirebaseIdToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user) throw new Error("No Firebase user signed in");
  return user.getIdToken();
}

/**
 * Returns true when an error indicates that the user's EEN credentials
 * are missing from the DB or have expired and cannot be refreshed.
 * This covers:
 *  - Backend 401 ("No Eagle Eye credentials found. Please authenticate first.")
 *  - EEN refresh token expiry (the token exchange itself fails with 4xx)
 */
function isEenAuthError(err: unknown): boolean {
  const message = err instanceof Error ? err.message.toLowerCase() : String(err).toLowerCase();
  return (
    message.includes("eagle eye credentials") ||
    message.includes("please authenticate") ||
    message.includes("no eagle eye") ||
    // Apollo wraps Hasura action 401s — the HTTP status lands in networkError
    (err as any)?.networkError?.statusCode === 401 ||
    (err as any)?.networkError?.result?.message?.toLowerCase().includes("authenticate")
  );
}

/**
 * Custom hook for making authenticated requests to Eagle Eye Network API
 */
export function useEagleEyeApi(options: UseEagleEyeApiOptions = {}) {
  const { isAuthenticated, clearAuthData } = useEagleEyeNetworkStore();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchEenVideo] = useSearchEenVideoMutation();
  const [getEenCameras] = useGetEenCamerasMutation();
  const [getEenCameraById] = useGetEenCameraByIdMutation();
  const [listEenMedia] = useListEenMediaMutation();

  // Keep onUnauthorized in a ref so callbacks never go stale inside memoized functions
  const onUnauthorizedRef = useRef(options.onUnauthorized);
  useEffect(() => {
    onUnauthorizedRef.current = options.onUnauthorized;
  }, [options.onUnauthorized]);

  /**
   * Called whenever an EEN auth failure is detected.
   * Clears the store (shows login screen) and fires the optional callback.
   */
  const handleAuthFailure = useCallback(() => {
    clearAuthData();
    onUnauthorizedRef.current?.();
  }, [clearAuthData]);

  /**
   * Fetches an EEN frame image via POST proxy and returns a blob URL for use as an <img> src.
   * Uses Firebase ID token for authentication — the backend resolves the EEN token from DB.
   * Caller is responsible for revoking the blob URL with URL.revokeObjectURL() when done.
   */
  const fetchVideoThumbnail = useCallback(
    async (httpsUrl: string): Promise<string> => {
      const idToken = await getFirebaseIdToken();
      const apiUrl = process.env.REACT_APP_API_URL ?? "";
      const res = await fetch(`${apiUrl}/een/image-proxy`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${idToken}`,
        },
        body: JSON.stringify({ url: httpsUrl }),
      });

      if (res.status === 401) {
        handleAuthFailure();
        throw new Error("EEN session expired. Please re-authenticate with Eagle Eye Network.");
      }

      if (!res.ok) throw new Error(`Image proxy error: ${res.status}`);
      const blob = await res.blob();
      return URL.createObjectURL(blob);
    },
    [handleAuthFailure]
  );

  /**
   * Returns the EEN HLS source URL for use with HlsVideoPlayer.
   * The player's custom loader fetches a fresh Firebase ID token per request
   * and routes all EEN traffic through the POST video proxy.
   */
  const fetchVideoProxy = useCallback(
    (hlsUrl: string): { src: string } => {
      return { src: hlsUrl };
    },
    []
  );

  /**
   * Generic API call wrapper with loading and error state management.
   * Automatically detects EEN auth failures and forces re-login.
   */
  const executeApiCall = useCallback(
    async <T = any>(apiCall: () => Promise<T>): Promise<T | null> => {
      setIsLoading(true);
      setError(null);

      try {
        const result = await apiCall();
        return result;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Unknown error occurred";
        setError(errorMessage);

        if (isEenAuthError(err)) {
          handleAuthFailure();
        }

        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [handleAuthFailure]
  );

  /**
   * Get all cameras from Eagle Eye Network via server-side proxy.
   * Optionally accepts search/filter params.
   */
  const getCameras = useCallback(
    (params: CameraSearchParams = {}) => {
      return executeApiCall(async () => {
        const result = await getEenCameras({
          variables: {
            input: {
              q: params.q,
              name__contains: params.name__contains,
              status__in: params.status__in?.join(","),
              locationId__in: params.locationId__in?.join(","),
              tags__any: params.tags__any?.join(","),
              pageSize: params.pageSize,
            },
          },
        });

        if (result.data?.een_get_cameras?.data) {
          return JSON.parse(result.data.een_get_cameras.data);
        }
        return null;
      });
    },
    [executeApiCall, getEenCameras]
  );

  /**
   * Get a specific camera by ID via server-side proxy.
   */
  const getCameraById = useCallback(
    (cameraId: string) => {
      return executeApiCall(async () => {
        const result = await getEenCameraById({
          variables: {
            input: {
              cameraId,
            },
          },
        });

        if (result.data?.een_get_camera_by_id?.data) {
          return JSON.parse(result.data.een_get_camera_by_id.data);
        }
        return null;
      });
    },
    [executeApiCall, getEenCameraById]
  );

  /**
   * List media recording intervals for a device via server-side proxy.
   * Pass mostRecentOnly: true to receive only the single most recent result.
   */
  const listMedia = useCallback(
    (params: ListMediaParams) => {
      return executeApiCall(async () => {
        const result = await listEenMedia({
          variables: {
            input: {
              deviceId: params.deviceId,
              type: params.type,
              mediaType: params.mediaType,
              startTimestampGte: params.startTimestampGte,
              endTimestampLte: params.endTimestampLte,
              coalesce: params.coalesce,
              pageSize: params.pageSize,
              pageToken: params.pageToken,
              mostRecentOnly: params.mostRecentOnly,
            },
          },
        });

        if (result.data?.een_list_media?.data) {
          return JSON.parse(result.data.een_list_media.data);
        }
        return null;
      });
    },
    [executeApiCall, listEenMedia]
  );

  /**
   * Search / filter cameras — alias for getCameras with params.
   */
  const searchCameras = useCallback(
    (params: CameraSearchParams) => getCameras(params),
    [getCameras]
  );

  /**
   * Video search using natural language queries via server-side proxy.
   * Calls EEN parse → deepSearch on the backend to avoid CORS.
   */
  const searchVideo = useCallback(
    (query: string, cameraIds?: string[], timeRange?: { start: string; end: string }) => {
      const now = new Date();
      const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const actorIn = cameraIds?.length
        ? cameraIds.map((id) => `camera:${id}`).join(",")
        : undefined;

      return executeApiCall(async () => {
        const result = await searchEenVideo({
          variables: {
            input: {
              query,
              timestampGte: timeRange?.start ?? sevenDaysAgo.toISOString(),
              timestampLte: timeRange?.end ?? now.toISOString(),
              actorIn,
            },
          },
        });

        if (result.data?.een_video_search?.data) {
          return JSON.parse(result.data.een_video_search.data);
        }
        return null;
      });
    },
    [executeApiCall, searchEenVideo]
  );

  return {
    // State
    isLoading,
    error,
    isAuthenticated,

    // API Methods
    getCameras,
    getCameraById,
    listMedia,
    searchCameras,
    searchVideo,

    // Proxy helpers
    fetchVideoThumbnail,
    fetchVideoProxy,
  };
}
